/**
 * Copyright ToolBelt Data Inc., 2020 All Rights Reserved
 */

import React, {useEffect, useState} from 'react';
import {useNavigate, useParams} from "react-router-dom";
import {useDispatch, useSelector} from "react-redux";
import { createAsyncThunk } from "@reduxjs/toolkit";

import {
    getOrganizationById,
    editOrganization,
    clearState,
} from "./managedOrganizationSlice";

import { getNewOrgDefaultSubscription } from "./organizationsSlice";

import {
    getAccountById
} from "../accounts/managedAccountSlice";

import {Row, Col, Button, Form, Table} from 'react-bootstrap';
import Alert from 'react-bootstrap/Alert';

import BreadCrumb from '../../common/components/BreadCrumb';
import TextHoverDisplay from "../../common/components/TextHoverDisplay";
import SpinnerOverlay from "../../common/components/SpinnerOverlay";
import PanelHeader from "../../common/components/layout/PanelHeader";
import FormTextInput from "../../common/components/FormTextInput";

import Container from "../../common/components/layout/Container";
import Panel from "../../common/components/layout/Panel";
import FormContainer from "../../common/components/layout/FormContainer";
import FormLabel from "../../common/components/form/FormLabel";
import FormJsonInput from "../../common/components/form/FormJsonInput";
import OrganizationConfigForm from './OrganizationConfigForm';
import styled from "styled-components";
import {isNumber} from "lodash";
import defaultConfig from "./organization-default-config.json";

const EditOrganizationContainer = (props) => {
    const { id } = useParams();
    const managedOrganizationState = useSelector(state => state.managedOrganization);
    const managedAccountState = useSelector(state => state.managedAccount);
    const organizationsState = useSelector(state => state.organizations);
    const dispatch = useDispatch();
    const navigate = useNavigate();

    const [showAlert, setShowAlert] = useState(false);
    const [alertMessage, setAlertMessage] = useState('');
    const [showSuccess, setShowSuccess] = useState(false);
    const [successMessage, setSuccessMessage] = useState('');

    const [breadcrumbItems, setBreadcrumbItems] = useState([]);

    // Form
    const [managedOrganization, setManagedOrganization] = useState({
        name: '',
    });
    const [name, setName] = useState('');
    const [ownerId, setOwnerId] = useState('');
    const [ownerName, setOwnerName] = useState('');
    const [config, setConfig] = useState(null);
    const [subscription, setSubscription] = useState(null);

    useEffect( () => {
            if (id){
                // dispatch(getOrganizationById(id));

                dispatch(getOrganizationById(id))
                    .unwrap()
                    .then((org) => {
                        dispatch(getAccountById(org.ownerId));
                    });
            }

        }, [id, dispatch]);

    // Load the base subscription so product display names (sourced from the DB
    // via the API) are available for the saved subscription being edited.
    useEffect(() => {
        dispatch(getNewOrgDefaultSubscription());
    }, [dispatch]);

    useEffect( () => {
        if (managedOrganizationState.managedOrganization) {
            setManagedOrganization(managedOrganizationState.managedOrganization);
            setName(managedOrganizationState.managedOrganization?.name);
            setOwnerId(managedOrganizationState.managedOrganization?.ownerId);
            setOwnerName(managedAccountState.managedAccount?.name);
            setConfig(managedOrganizationState.managedOrganization?.config);
            setSubscription(managedOrganizationState.managedOrganization?.subscription);
            setBreadcrumbItems(
                [
                    {label: 'Home', href: '/home', isActive: false},
                    {
                        label: 'Organizations',
                        href: '/admin/organizations',
                        isActive: false
                    },
                    {
                        label: `${managedOrganizationState?.managedOrganization.id}`,
                        href: '',
                        isActive: true
                    },
                    {label: 'Edit Organization' , href: '', isActive: true}]
            )
        }
        }
        , [managedOrganizationState.managedOrganization]);

    useEffect( () => {
            if (managedAccountState.managedAccount) {
                setOwnerName(`${managedAccountState.managedAccount?.givenName} ${managedAccountState.managedAccount?.surName}`);
            }
        }
        , [managedAccountState.managedAccount]);

    useEffect(() => {
        setAlertMessage(managedOrganizationState.alertMessage);
        if (managedOrganizationState.alertMessage) {
            setShowAlert(true);
        } else {
            setShowAlert(false);
        }
    }, [managedOrganizationState.alertMessage]);

    useEffect(() => {
        setSuccessMessage(managedOrganizationState.successMessage);
        if (managedOrganizationState.successMessage) {
            setShowSuccess(true);
        }else {
            setShowSuccess(false);
        }
    }, [managedOrganizationState.successMessage]);

    const handleEditOrganization = async (data) => {
        dispatch(editOrganization({id: id, data:data}));
    };

    const handleCancel = async () => {
        dispatch(clearState());
        navigate('/admin/organizations')
    };

    return (
        <div>
            {managedOrganizationState.loading && (
                <SpinnerOverlay />
            )}
            <Alert
                variant={"danger"}
                show={showAlert}
                onClose={() => setShowAlert(!showAlert)}
                dismissible
                transition={false}
            >
                {alertMessage}
            </Alert>
            <Alert
                variant={"success"}
                show={showSuccess}
                onClose={() => setShowSuccess(!showSuccess)}
                dismissible
                transition={false}
            >
                {successMessage}
            </Alert>
            <EditOrganization
                id={managedOrganization?.id}
                handleEditOrganization={(data) => handleEditOrganization(data)}
                handleCancel={() => handleCancel()}
                name={name}
                config={config}
                subscription={subscription}
                defaultSubscription={organizationsState?.defaultSubscription}
                breadcrumbItems={breadcrumbItems}
                ownerId={ownerId}
                ownerName={ownerName}
            />
        </div>
    );
};


function EditOrganization(props) {
    const {
        breadcrumbItems,
        name,
        ownerId,
        ownerName,
        handleCancel,
        id,
        config,
        subscription,
        defaultSubscription,
    } = props;

    const [formValid, setFormValid] = useState(true);
    const [nameState, setNameState] = useState('');
    const [configState, setConfigState] = useState(null);
    const [subscriptionState, setSubscriptionState] = useState(null);

    const reset = () => {
        setNameState('');
        setConfigState(null);
        setSubscriptionState(null);
    };

    const isValid = () => {
        if (!nameState || nameState.length == 0) {
            return false;
        }

        return true;
    };

    const handleEditOrganization = async () => {
        if (!isValid()) {
            setFormValid(false);
        } else {
            const data = {
                name: nameState,
                config: null,
                subscription: subscriptionState
            };
            props.handleEditOrganization(data);
        }
    };

    useEffect(() => {
        setNameState(name);
        setConfigState(config);
        setSubscriptionState(subscription);

    }, [name, config, subscription ]);

    const PreLikeText = styled.div `
        margin-top: 5px;
        display: block;
        white-space: pre-wrap;
        unicode-bidi: embed;
        font-family: monospace;
        color: #212529;
        font-size: 87.5%;
        `;

    return (
        <Container>
            <BreadCrumb items={breadcrumbItems}> </BreadCrumb>
            <PanelHeader
                header={'Edit Organization'}
                subHeader={'Change organization properties'}
            />
            <Panel>
                <FormContainer>
                    <Form noValidate>
                        <Form.Group controlId="editOrganization">
                            <Row>
                                <Col md={9}>
                                    <div className="organization-section">
                                        <Row>
                                            <Col md={4}>
                                                <FormLabel>Organization ID</FormLabel>
                                                {id &&
                                                    <TextHoverDisplay
                                                        fullText={id}
                                                        truncatedText={id}
                                                        displayTruncated={false}
                                                    />
                                                }
                                            </Col>
                                            <Col md={4}>
                                                <FormLabel className={"formTextLabel"}>Organization Owner ID</FormLabel>
                                                {id &&
                                                    <TextHoverDisplay
                                                        fullText={ownerId}
                                                        truncatedText={ownerId}
                                                        displayTruncated={false}
                                                    />
                                                }
                                            </Col>
                                            <Col md={4}>
                                                <FormLabel className={"formTextLabel"}>Organization Owner Name</FormLabel>
                                                {id &&
                                                    <TextHoverDisplay
                                                        fullText={ownerName}
                                                        truncatedText={ownerName}
                                                        displayTruncated={false}
                                                    />
                                                }
                                            </Col>
                                        </Row>
                                        <Row>
                                            <Col md={12}>
                                                <FormTextInput
                                                    label={'Name'}
                                                    placeholder="Enter name"
                                                    value={nameState}
                                                    onChange={value => setNameState(value)}
                                                    isInvalid={!formValid && !nameState}
                                                    size={'sm'}
                                                    required
                                                    height={36}
                                                />
                                            </Col>
                                        </Row>
                                    </div>
                                    <Row>
                                        <Col>
                                           <OrganizationConfigForm
                                                setSubscription={setSubscriptionState}
                                                setConfig={setConfigState}
                                                formValid={formValid}
                                                subscription={subscriptionState}
                                                defaultSubscription={defaultSubscription}
                                            />
                                        </Col>
                                    </Row>
                                    <Row>
                                        <Col xl={12} lg={12} md={12} sm={12} className="buttons">
                                            <Button
                                                className="btn-min-width-120 btn-height-35"
                                                variant="info"
                                                onClick={() => handleEditOrganization()}
                                            >EDIT
                                            </Button>
                                            <Button
                                                className="btn-min-width-120 btn-height-35"
                                                style={{marginLeft: "10px"}}
                                                variant="outline-secondary"
                                                onClick={() => {
                                                    reset();
                                                    handleCancel();

                                                }}
                                            >CANCEL
                                            </Button>
                                        </Col>
                                    </Row>
                                </Col>
                                <Col md={3}>
                                    <div style={{position: 'sticky', top: 0, padding: '15px'}}>
                                        <h6 style={{fontWeight: "600"}}>Help</h6>
                                        <PreLikeText>
                                            <Table size="sm">
                                                <thead>
                                                <tr>
                                                    <th>Property</th>
                                                    <th>Description</th>
                                                </tr>
                                                </thead>
                                                <tbody>
                                                <tr>
                                                    <td style={{padding: '5px'}}>name</td>
                                                    <td style={{padding: '5px'}}>The organization name</td>
                                                </tr>
                                                <tr>
                                                    <td style={{padding: '5px'}}>companyScore</td>
                                                    <td style={{padding: '5px'}}>Numeric value in range 0.6 - 0.9. Default value is 0.8</td>
                                                </tr>
                                                <tr>
                                                    <td style={{padding: '5px'}}>contactScore</td>
                                                    <td style={{padding: '5px'}}>Numeric value in range 0.6 - 0.9. Default value is 0.8</td>
                                                </tr>
                                                <tr>
                                                    <td style={{padding: '5px'}}>allowUnfiltered</td>
                                                    <td style={{padding: '5px'}}>Allows unfiltered scores. Default value is false</td>
                                                </tr>
                                                <tr>
                                                    <td style={{padding: '5px'}}>features</td>
                                                    <td style={{padding: '5px'}}>Options are Search, Enrich, and Export</td>
                                                </tr>
                                                <tr>
                                                    <td style={{padding: '5px'}}>segments</td>
                                                    <td style={{padding: '5px'}}>Currently disabled</td>
                                                </tr>
                                                <tr>
                                                    <td style={{padding: '5px'}}>classifications</td>
                                                    <td style={{padding: '5px'}}>Uncheck unrestricted to select from available classifications</td>
                                                </tr>
                                                <tr>
                                                    <td style={{padding: '5px'}}>services</td>
                                                    <td style={{padding: '5px'}}>Currently disabled</td>
                                                </tr>
                                                <tr>
                                                    <td style={{padding: '5px'}}>unrestricted</td>
                                                    <td style={{padding: '5px'}}>Default is selected. Deselect to edit Taxonomies fields</td>
                                                </tr>
                                                <tr>
                                                    <td style={{padding: '5px'}}>cities</td>
                                                    <td style={{padding: '5px'}}>Uncheck unrestricted to enter cities (separated by commas)</td>
                                                </tr>
                                                <tr>
                                                    <td style={{padding: '5px'}}>msa</td>
                                                    <td style={{padding: '5px'}}>Uncheck unrestricted to select from available msa's</td>
                                                </tr>
                                                <tr>
                                                    <td style={{padding: '5px'}}>regions</td>
                                                    <td style={{padding: '5px'}}>Uncheck unrestricted to select from available regions</td>
                                                </tr>
                                                <tr>
                                                    <td style={{padding: '5px'}}>unrestricted</td>
                                                    <td style={{padding: '5px'}}>Default is selected. Deselect to edit Geographies fields</td>
                                                </tr>
                                                <tr>
                                                    <td style={{padding: '5px'}}>suppliers</td>
                                                    <td style={{padding: '5px'}}>Put something here</td>
                                                </tr>
                                                </tbody>
                                            </Table>
                                        </PreLikeText>
                                    </div>
                                </Col>
                            </Row>
                        </Form.Group>
                    </Form>
                </FormContainer>
            </Panel>
        </Container>
    );
}

export default EditOrganizationContainer;

